import axios from 'axios';
import { SLOW_RESPONSE_THRESHOLD, REQUEST_RETRIES, INITIAL_DELAY_BETWEEN_RETRIES } from './constants';
import logger from './logger';
import { wrapTimeout } from './wrapTimeout';
import { EventPayload } from './structureData';
import { GenericObject } from './helperTypes';
import { EndpointOptions } from './initializer';

interface RequestSpecs {
    url: string;
    data?: EventPayload[];
    options?: GenericObject;
}

interface ServerResponse {
    data: any;
}

const sendRequest = async ({ url, data, options = {} }: RequestSpecs): Promise<ServerResponse | Error> => {

    // Start countdown till error for slow response:
    const timeoutId = setSlowResponseDetection(url);

    // Send data to backend:
    const response = await cycleRequest({ url, data, options, retriesRemaining: REQUEST_RETRIES, delay: INITIAL_DELAY_BETWEEN_RETRIES });

    // Clear countdown till error from slow response:
    clearTimeout(timeoutId);

    return response;
}

interface CycleRequestSpecs extends RequestSpecs {
    options: EndpointOptions;
    retriesRemaining: number;
    delay: number;
};
type CycleRequest = (requestSpecs: CycleRequestSpecs) => Promise<ServerResponse | Error>;
const cycleRequest: CycleRequest = async ({ url, options, data, retriesRemaining, delay }) => {
    // Try to send, and recurse if it fails:
    let response;
    try {
        // Attempt a send:
        const method = data ? 'post' : 'get';
        const withCredentials = options.withCredentials === true ? true : false;
        const headers = options.headers ? options.headers : {};
        response = await axios({ method, url, data, withCredentials, headers });
    } catch (error) {
        response = await tryAgain({ retriesRemaining, delay, url, options, data, error });
    }

    return response;
};

interface TryAgainSpecs extends CycleRequestSpecs {
    error: Error;
}
type TryAgain = (requestSpecs: TryAgainSpecs) => Promise<ServerResponse | Error>;
const tryAgain: TryAgain = async ({ retriesRemaining, delay, url, options, data, error }) => {
    // If the request failed, error then continue trying till success or retries are exhausted.
    if (retriesRemaining === REQUEST_RETRIES) {
        // To avoid too much noise if all of the retries fail, we'll only throw an error the first time and the last time.
        error.message = `AF's first request to backend failed. Retrying. ${error.message}`;
        logger.log(error);
    } else if (retriesRemaining === 0) {
        // We have exhausted our retries.  Give up and alert the error:
        error.message = `AF request to backend failed all ${REQUEST_RETRIES} retries. ${error.message}`;
        logger.error(error);
        return error;
    }
    // Decrement retry counter, wait for a bit, then try again:
    const newRetriesRemaining = retriesRemaining - 1;
    const newDelay = delay * 2;
    return wrapTimeout(() => cycleRequest({ url, options, data, retriesRemaining: newRetriesRemaining, delay: newDelay }), delay);
}

const setSlowResponseDetection = (url: string) => setTimeout(() => {
    // Send an error if the request takes too long:
    logger.log(new Error(`Request to ${url} took greater than ${SLOW_RESPONSE_THRESHOLD} to complete`));
}, SLOW_RESPONSE_THRESHOLD);

export default sendRequest;
